import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { getDocumentVisibilityCardBadge } from './documentVisibility';
import {
  HiDocumentText,
  HiEye,
  HiArrowDownTray,
  HiLink,
  HiPencilSquare,
  HiArrowPath,
  HiTrash,
  HiInformationCircle,
  HiGlobeAlt,
  HiChevronDown,
  HiChevronUp,
} from 'react-icons/hi2';

const MAX_NAME_LINES = 2;
const MAX_NOTES_LENGTH = 80;

export default function DocumentCard({
  doc,
  isPreviewable,
  canManageDoc,
  openDetails,
  openPreview,
  openShare,
  openRename,
  openReupload,
  onDelete,
  formatFileSize,
  formatDate,
}) {
  const [notesExpanded, setNotesExpanded] = useState(false);
  const isLinkDocument = doc.isLink === true || doc.linkType;

  const getLinkTypeLabel = () => {
    if (doc.linkType === 'google-drive') return 'Google Drive';
    if (doc.linkType === 'github') return 'GitHub';
    if (doc.linkType === 'external-link') return 'External Link';
    return 'Link';
  };

  const notes = doc.notes?.trim() || '';
  const notesTruncated = notes.length > MAX_NOTES_LENGTH;
  const notesDisplay =
    notesExpanded || !notesTruncated ? notes : `${notes.slice(0, MAX_NOTES_LENGTH)}...`;

  const visBadge = getDocumentVisibilityCardBadge(doc);

  return (
    <motion.div
      layout
      whileHover={{ y: -4 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md shadow-gray-200/50 ring-1 ring-transparent transition-all hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/10 hover:ring-blue-100 sm:border-gray-200"
    >
      {/* Fixed card height wrapper - ensures same size */}
      <div className="flex h-full min-h-[380px] flex-col">
        {/* Quick actions - always visible on mobile, hover on desktop */}
        <div className="absolute right-3 top-3 z-10 flex gap-2 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
          {canManageDoc(doc) && (
            <>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => openReupload(doc)}
                className="rounded-lg bg-white p-2 text-gray-600 shadow-lg ring-1 ring-black/5 transition-colors hover:bg-blue-50 hover:text-blue-600"
                title="Re-upload"
                aria-label="Re-upload"
              >
                <HiArrowPath className="h-4 w-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => onDelete(doc)}
                className="rounded-lg bg-white p-2 text-red-600 shadow-lg ring-1 ring-black/5 transition-colors hover:bg-red-50"
                title="Delete"
                aria-label="Delete"
              >
                <HiTrash className="h-4 w-4" />
              </motion.button>
            </>
          )}
        </div>

        {/* Preview section - fixed height */}
        <div className="flex-shrink-0 px-4 pt-4">
          {isLinkDocument ? (
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="flex h-40 cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gradient-to-br from-blue-50 to-white shadow-inner transition-all hover:border-blue-400 sm:h-44"
              onClick={() => window.open(doc.url, '_blank')}
            >
              <div className="text-center">
                <HiGlobeAlt className="mx-auto h-12 w-12 text-blue-600 sm:h-14 sm:w-14" />
                <p className="mt-2 text-xs font-semibold text-blue-700 sm:text-sm">
                  {getLinkTypeLabel()}
                </p>
              </div>
            </motion.div>
          ) : isPreviewable(doc.fileType) ? (
            <div className="group/preview relative overflow-hidden rounded-xl border border-gray-200 shadow-inner">
              {doc.fileType?.startsWith('image/') ? (
                <motion.img
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                  src={doc.url}
                  alt={doc.fileName}
                  className="h-40 w-full cursor-pointer object-cover sm:h-44"
                  onClick={() => openPreview(doc)}
                />
              ) : (
                <div className="relative flex h-40 w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 sm:h-44">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: 'spring' }}
                  >
                    <HiDocumentText className="h-12 w-12 text-gray-400 sm:h-14 sm:w-14" />
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm"
                  >
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => openPreview(doc)}
                      className="rounded-full bg-white p-3 text-black shadow-xl transition-colors hover:bg-gray-50"
                      title="Preview"
                      aria-label="Preview"
                    >
                      <HiEye className="h-5 w-5" />
                    </motion.button>
                  </motion.div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex h-40 items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gradient-to-br from-white to-gray-50 shadow-inner sm:h-44">
              <HiDocumentText className="h-12 w-12 text-gray-400 sm:h-14 sm:w-14" />
            </div>
          )}
        </div>

        {/* File info - flex-1 with read more */}
        <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 pt-3">
          <div className="min-h-0 flex-1 space-y-2">
            <h3
              className={`font-semibold text-gray-900 md:text-base ${
                (doc.fileName || '').length > 50 ? 'line-clamp-2' : 'line-clamp-2'
              }`}
              title={doc.fileName}
            >
              {doc.fileName}
            </h3>
            <div className="flex flex-wrap gap-1">
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                {doc.category}
              </span>
              {isLinkDocument && (
                <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800">
                  {getLinkTypeLabel()}
                </span>
              )}
              {!isLinkDocument && (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                  {formatFileSize(doc.fileSize)}
                </span>
              )}
              {visBadge && <span className={visBadge.className}>{visBadge.label}</span>}
            </div>

            {notes && (
              <div className="text-xs text-gray-500">
                <p className={`text-gray-600 ${notesExpanded ? '' : 'line-clamp-2'}`}>
                  {notesDisplay}
                </p>
                {notesTruncated && (
                  <button
                    type="button"
                    onClick={() => setNotesExpanded(!notesExpanded)}
                    className="mt-1 inline-flex items-center gap-0.5 font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {notesExpanded ? (
                      <>
                        Read less <HiChevronUp className="h-3.5 w-3.5" />
                      </>
                    ) : (
                      <>
                        Read more <HiChevronDown className="h-3.5 w-3.5" />
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>{formatDate(doc.uploadedAt)}</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => openDetails(doc)}
              className="rounded-lg bg-blue-50 p-2 text-blue-600 shadow-sm transition-all hover:bg-blue-100 hover:shadow-md"
              title="View Details"
              aria-label="View details"
            >
              <HiInformationCircle className="h-4 w-4 sm:h-5 sm:w-5" />
            </motion.button>
            {!isLinkDocument && isPreviewable(doc.fileType) && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => openPreview(doc)}
                className="rounded-lg bg-green-50 p-2 text-green-600 shadow-sm transition-all hover:bg-green-100 hover:shadow-md"
                title="Preview"
                aria-label="Preview"
              >
                <HiEye className="h-4 w-4 sm:h-5 sm:w-5" />
              </motion.button>
            )}
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href={doc.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg bg-blue-50 p-2 text-blue-600 shadow-sm transition-all hover:bg-blue-100 hover:shadow-md"
              title={isLinkDocument ? 'Open Link' : 'Download'}
              aria-label={isLinkDocument ? 'Open link' : 'Download'}
            >
              {isLinkDocument ? (
                <HiGlobeAlt className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <HiArrowDownTray className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </motion.a>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => openShare(doc)}
              className="rounded-lg bg-gray-50 p-2 text-gray-700 shadow-sm transition-all hover:bg-gray-100 hover:shadow-md"
              title="Share"
              aria-label="Share"
            >
              <HiLink className="h-4 w-4 sm:h-5 sm:w-5" />
            </motion.button>
            {canManageDoc(doc) && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => openRename(doc)}
                className="rounded-lg bg-gray-50 p-2 text-gray-700 shadow-sm transition-all hover:bg-gray-100 hover:shadow-md"
                title="Rename"
                aria-label="Rename"
              >
                <HiPencilSquare className="h-4 w-4 sm:h-5 sm:w-5" />
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
